import {useQuery} from "@tanstack/react-query"
import {studentService} from "services"
import {BaseDepartmentId} from "types/departments"
import {useMemo} from "react"
import {checkPermission} from "helpers"
import {Permissions, PermissionUserType} from "types/permission"
import {Model} from "Model"
import {AppliedFilter, FilterList} from "types/filter"
import moment from "moment"

const queryCacheKey = "department-student"

type DepartmentStudentProps = {
    studentId?: number
    departmentId?: BaseDepartmentId | number
    isEnabled?: boolean
}

export const useDepartmentStudent = ({studentId, departmentId, isEnabled = true}: DepartmentStudentProps) => {
    const query = useQuery(
        [queryCacheKey, studentId, departmentId],
        async () => {
            const {
                data: [student]
            } = await studentService.getDepartmentStudents({
                skipTotal: true,
                filter: {
                    departmentId,
                    profileIds: [studentId]
                }
            })
            return student
        },
        {
            enabled: !!studentId && isEnabled
        }
    )

    return {
        student: query.data,
        isLoading: query.isFetching
    }
}

export const useCanShowAllStudents = (model: Model, departmentId: number) =>
    useMemo(() => {
        let permissions: PermissionUserType = {}
        switch (departmentId) {
            case BaseDepartmentId.FinancialAid:
                permissions = {staff: [Permissions.Staff.FinancialAid.Director.View]}
                break
            case BaseDepartmentId.StudentAccount:
                permissions = {staff: [Permissions.Staff.StudentAccount.Director.View]}
                break
            case BaseDepartmentId.Academics:
            case BaseDepartmentId.AcademicAffair:
            case BaseDepartmentId.StudentServices:
                permissions = {staff: [Permissions.Staff.StudentServicesAndAcademicAffairs.Director.View]}
                break
            case BaseDepartmentId.Admissions:
            default:
                permissions = {staff: [Permissions.Staff.Admissions.Director.View]}
        }
        return checkPermission(permissions, model)
    }, [model, departmentId])

export const useCanHaveAccessOthers = (model: Model, departmentId: number) =>
    useMemo(() => {
        let permissions: PermissionUserType = {}
        switch (departmentId) {
            case BaseDepartmentId.FinancialAid:
            case BaseDepartmentId.StudentAccount:
                permissions = {staff: [Permissions.Staff.FinancialAid.Students.PermissionsToAccessOthers.View]}
                break
            case BaseDepartmentId.Academics:
            case BaseDepartmentId.AcademicAffair:
            case BaseDepartmentId.StudentServices:
                permissions = {
                    staff: [Permissions.Staff.Academics.Instructional.Students.PermissionsToAccessOthers.View]
                }
                break
            case BaseDepartmentId.Admissions:
            default:
                permissions = {staff: [Permissions.Staff.Admissions.Students.PermissionsToAccessOthers.View]}
        }
        return checkPermission(permissions, model)
    }, [model, departmentId])

export const useStudentListV2Filters = (filters: AppliedFilter["filters"]) =>
    useMemo(() => {
        const filter: FilterList = {}

        const {
            profiles,
            campus,
            program,
            acadAdvisors,
            faAdvisors,
            admAdvisors,
            term,
            startingTerm,
            checklist,
            status,
            state,
            profileStatus,
            applicationCompletedDate,
            startDateRange,
            createdAtRange,
            inquiryDateRange,
            lastActivityDateRange,
            includeArchive,
            showDueOnly,
            fullyEnrolled,
            onlySEV,
            sapCheckpoints,
            retentionLevels,
            studentStatusIds,
            admissionsLeadSourceIds,
            leadTypeIds,
            isAccessDisabled,
            phases,
            shifts,
            activityIds,
            activityDescription,
            temporaryOutDateRange,
            withdrawalDateRange,
            users,
            countries,
            attendancePercentage
        } = filters

        if (fullyEnrolled) filter.fullyEnrolled = fullyEnrolled
        if (includeArchive) filter.includeArchive = includeArchive
        if (showDueOnly) filter.showDueOnly = showDueOnly
        if (onlySEV) filter.onlySEV = onlySEV
        if (checklist) {
            filter.checklistCompleted = {
                operator: checklist.operator,
                value: checklist.value.id
            }
        }

        if (profiles) {
            filter.profileIds = {
                operator: profiles.operator,
                value: profiles.value.map(({profileId}) => profileId)
            }
        }
        if (campus) {
            filter.campusIds = {
                operator: campus.operator,
                value: campus.value.map(({id}) => id)
            }
        }
        if (program) {
            filter.programIds = {
                operator: program.operator,
                value: program.value.map(({id}) => id)
            }
        }
        if (studentStatusIds) {
            filter.studentStatusIds = {
                operator: studentStatusIds.operator,
                value: studentStatusIds.value.map(({statusId}) => statusId)
            }
        }
        if (admissionsLeadSourceIds) {
            filter.admissionsLeadSourceIds = {
                operator: admissionsLeadSourceIds.operator,
                value: admissionsLeadSourceIds.value.map(({id}) => id)
            }
        }
        if (leadTypeIds) {
            filter.leadTypeIds = {
                operator: leadTypeIds.operator,
                value: leadTypeIds.value.map(({leadTypeId}) => leadTypeId)
            }
        }
        if (isAccessDisabled) {
            filter.isAccessDisabled = {
                ...isAccessDisabled,
                value: !!isAccessDisabled?.value?.id
            }
        }
        if (phases) {
            filter.phases = {
                ...phases,
                value: phases.value.map(({phaseId}) => phaseId)
            }
        }
        if (shifts) {
            filter.shifts = {
                ...shifts,
                value: shifts.value.map(({shiftId}) => shiftId)
            }
        }

        if (admAdvisors) {
            filter.admAdvisorProfileIds = {
                operator: admAdvisors.operator,
                value: admAdvisors.value.map((profile) => profile.profileId)
            }
        }
        if (faAdvisors) {
            filter.faAdvisorProfileIds = {
                operator: faAdvisors.operator,
                value: faAdvisors.value.map((profile) => profile.profileId)
            }
        }
        if (acadAdvisors) {
            filter.acadAdvisorProfileIds = {
                operator: acadAdvisors.operator,
                value: acadAdvisors.value.map((profile) => profile.profileId)
            }
        }

        if (status) {
            filter.statusIds = {
                operator: status.operator,
                value: status.value.map(({statusId}) => statusId)
            }
        }
        if (state) {
            filter.states = {
                operator: state.operator,
                value: state.value.map(({id}) => id)
            }
        }
        if (profileStatus) {
            filter.profileStatuses = {
                operator: profileStatus.operator,
                value: profileStatus.value.map(({id}) => id === "active")
            }
        }
        if (term) {
            filter.termIds = {
                operator: term.operator,
                value: term.value.map(({id}) => id)
            }
        }
        if (startingTerm) {
            filter.startingTermIds = {
                operator: startingTerm.operator,
                value: startingTerm.value.map(({id}) => id)
            }
        }
        if (activityIds) {
            filter.activityIds = {
                operator: activityIds.operator,
                value: activityIds.value.map(({activityId}) => activityId)
            }
        }
        if (activityDescription) {
            filter.activityDescription = activityDescription
        }
        if (applicationCompletedDate) {
            filter.applicationCompletedDate = {
                operator: applicationCompletedDate.operator,
                value: Array.isArray(applicationCompletedDate.value)
                    ? applicationCompletedDate.value.map((v) => moment(v).format("YYYY-MM-DD"))
                    : moment(applicationCompletedDate.value).format("YYYY-MM-DD")
            }
        }
        if (startDateRange) {
            filter.startDateRange = {
                operator: startDateRange.operator,
                value: Array.isArray(startDateRange.value)
                    ? startDateRange.value.map((v) => moment(v).format("YYYY-MM-DD"))
                    : moment(startDateRange.value).format("YYYY-MM-DD")
            }
        }
        if (createdAtRange) {
            filter.createdAtRange = {
                operator: createdAtRange.operator,
                value: Array.isArray(createdAtRange.value)
                    ? createdAtRange.value.map((v) => moment(v).format("YYYY-MM-DD"))
                    : moment(createdAtRange.value).format("YYYY-MM-DD")
            }
        }
        if (inquiryDateRange) {
            filter.inquiryDateRange = {
                operator: inquiryDateRange.operator,
                value: Array.isArray(inquiryDateRange.value)
                    ? inquiryDateRange.value.map((v) => moment(v).format("YYYY-MM-DD"))
                    : moment(inquiryDateRange.value).format("YYYY-MM-DD")
            }
        }
        if (lastActivityDateRange) {
            filter.lastActivityDateRange = {
                operator: lastActivityDateRange.operator,
                value: Array.isArray(lastActivityDateRange.value)
                    ? lastActivityDateRange.value.map((v) => moment(v).format("YYYY-MM-DD"))
                    : moment(lastActivityDateRange.value).format("YYYY-MM-DD")
            }
        }
        if (sapCheckpoints) {
            filter.sapCheckpoints = {
                operator: sapCheckpoints.operator,
                value: sapCheckpoints.value.map(({id}) => id)
            }
        }
        if (retentionLevels) {
            filter.retentionLevels = {
                operator: retentionLevels.operator,
                value: retentionLevels.value.map(({id}) => id)
            }
        }

        if (temporaryOutDateRange) {
            filter.temporaryOutDateRange = {
                operator: temporaryOutDateRange.operator,
                value: Array.isArray(temporaryOutDateRange.value)
                    ? temporaryOutDateRange.value.map((v) => moment(v).format("YYYY-MM-DD"))
                    : moment(temporaryOutDateRange.value).format("YYYY-MM-DD")
            }
        }
        if (withdrawalDateRange) {
            filter.withdrawalDateRange = {
                operator: withdrawalDateRange.operator,
                value: Array.isArray(withdrawalDateRange.value)
                    ? withdrawalDateRange.value.map((v) => moment(v).format("YYYY-MM-DD"))
                    : moment(withdrawalDateRange.value).format("YYYY-MM-DD")
            }
        }

        if (users) {
            filter.userIds = {
                operator: users.operator,
                value: users.value.map(({id}) => id)
            }
        }

        if (countries) {
            filter.countryIds = {
                operator: countries.operator,
                value: countries.value.map(({name}) => name)
            }
        }

        if (attendancePercentage) {
            filter.attendancePercentage = attendancePercentage
        }

        return filter
    }, [filters])
